import { CasApplication } from '../../../coverpath-common/classes/coverpath_subclasses/CasApplication';
import { CoverpathBasePolicy } from '../../../coverpath-common/classes/coverpath_subclasses/CoverpathBasePolicy';
import { CoverpathPolicy } from '../../../coverpath-common/classes/coverpath_subclasses/CoverpathPolicy';
import { CoverpathTermQuote } from '../../../coverpath-common/classes/quote/coverpath/CoverpathTermQuote';
import { CoverpathWholeLifeQuote } from '../../../coverpath-common/classes/quote/coverpath/CoverpathWholeLifeQuote';
import { BaseQuote } from '../quote/BaseQuote';
import { Exchange1035 } from '../quote/coverpath/Exchange1035';
import { QuoteExchange1035 } from '../quote/coverpath/QuoteExchange1035';
import { Product, RiderType } from '../static/Product';
import { PolicyRider } from './PolicyRider';
import { Person } from './Person';
import { QuoteRider } from '../quote/QuoteRider';
import { LoanType } from '../../../coverpath-common/js/interfaces/CoverpathTypes';

export class SafeAdvisorQuote {
  hasBeenInitialized: boolean;
  productType: string;
  isJoint: boolean;
  term: number;

  constructor(unsafeAdvisorQuote: BaseQuote) {
    this.hasBeenInitialized = unsafeAdvisorQuote.hasBeenInitialized();
    this.productType = unsafeAdvisorQuote.productType;
    this.isJoint = !!unsafeAdvisorQuote.secondaryQuote;
    this.term = unsafeAdvisorQuote.term;
  }
}

export class SafeExchange1035 {
  cashValueTransfer: number;
  isMEC: boolean;
  costBasis: number;
  expectedArrival: number;
  applyTowards: string;

  constructor(alirRider: PolicyRider) {
    this.cashValueTransfer = alirRider.alir1035ExchangeAmountWithPendingAmendment();
    this.isMEC = alirRider.alir1035IsMecWithPendingAmendment();
    this.costBasis = alirRider.alir1035CostBasisWithPendingAmendment();
    this.expectedArrival = alirRider.alir1035ExpectedArrivalWithPendingAmendment();
    this.applyTowards = alirRider.alir1035ApplyTowardsWithPendingAmendment();
  }
}

export class SafeQuoteRider {
  premiumWaiver: number;
  type: string;
  amount: number;
  premiumBase: number;
  plannedYears: number;
  paymentFrequency: string;
  accelerationPercent: number;
  predictedBenefitAge: number;
  puaAddedToBenefitPool: boolean;
  monthlyIncreaseOption: boolean;

  constructor(rider: QuoteRider) {
    if (!!rider) {
      this.premiumWaiver = rider.premiumWaiver;
      this.type = rider.type;
      this.amount = rider.amount;
      this.premiumBase = rider.premiumBase;
      this.plannedYears = rider.plannedYears;
      this.paymentFrequency = rider.paymentFrequency;
      this.accelerationPercent = rider.benefitDisbursementMethod;
      this.predictedBenefitAge = rider.predictedBenefitAge;
      this.puaAddedToBenefitPool = rider.puaAddedToBenefitPool;
      this.monthlyIncreaseOption = rider.monthlyIncreaseOption;
    }
  }
}

export class SafePolicyRider {
  amount: number;
  premium: number;
  premiumBase: number;
  type: string;
  hasALIRExchange1035: boolean;
  exchange1035: SafeExchange1035;
  premiumWaiver: number;
  plannedYears: number;
  paymentFrequency: string;
  lumpSumUsers1035: boolean;
  accelerationPercent: number;
  predictedBenefitAge: number;
  puaAddedToBenefitPool: boolean;
  monthlyIncreaseOption: boolean;

  constructor(rider: PolicyRider) {
    this.amount = rider.amountWithPendingAmendment();
    this.type = rider.type;
    this.premiumBase = rider.premiumBaseWithPendingAmendment();
    this.premium = rider.premiumBaseWithPendingAmendment();
    this.premiumWaiver = rider.premiumWaiverWithPendingAmendment();
    this.plannedYears = rider.plannedYearsWithPendingAmendment();
    this.paymentFrequency = rider.paymentFrequencyWithPendingAmendment();
    this.lumpSumUsers1035 = rider.hasAlir1035ExchangeWithPendingAmendment();

    this.accelerationPercent = rider.benefitDisbursementMethod;
    this.predictedBenefitAge = rider.predictedBenefitAge;
    this.puaAddedToBenefitPool = rider.puaAddedToBenefitPool;
    this.monthlyIncreaseOption = rider.monthlyIncreaseOption;

    this.hasALIRExchange1035 = this.lumpSumUsers1035 && !!rider.exchange1035;
    if (this.hasALIRExchange1035) {
      this.exchange1035 = new SafeExchange1035(rider);
    }
  }
}

export class SafePerson {
  firstName: string;
  lastName: string;
  fullName: string;
  gender: string;
  dob: Date;
  email: string;
  emailName: string;

  constructor(person?: Person) {
    if (!!person) {
      this.firstName = person.firstName;
      this.dob = person.dob;
      this.fullName = person.fullName;
      this.gender = person.gender;
      this.lastName = person.lastName;
      this.email = person.email;
      this.emailName = person.emailName;
    }
  }
}

export class SafeBasePolicy {
  hasWOP: boolean;
  WOPValue: number;

  insured: SafePerson;
  owner: SafePerson;
  customer: SafePerson;
  term: number;

  faceAmount: number;
  product: string;
  productType: string;
  paymentFrequency: string;

  effectiveDate: Date | null;
  loanType: string;
  dividendOption: string;
  healthClass: string;
  flatExtra: number;
  contractState: string;
  constructor(policy: CoverpathPolicy) {
    if (!!policy.insured) {
      this.insured = new SafePerson(policy.insured.person);
    }
    if (!!policy.ownerPerson) {
      this.owner = new SafePerson(policy.ownerPerson);
    }
    if (!!policy.customer && !!policy.customer.primaryCustomer) {
      this.customer = new SafePerson(policy.customer.primaryCustomer.person);
    }

    this.hasWOP = policy.hasWaiverWithPendingAmendment();
    this.WOPValue = !!policy.premiumBreakdown ? policy.premiumBreakdown.waiverAmountWithPendingAmendment() : 0;

    this.term = policy.termWithPendingAmendment();

    this.faceAmount = policy.faceAmountWithPendingAmendment();

    this.product = policy.productWithPendingAmendment();
    this.productType = policy.productTypeWithPendingAmendment();
    this.paymentFrequency = policy.paymentFrequencyWithPendingAmendment();

    this.dividendOption = policy.dividendOptionWithPendingAmendment();
    this.effectiveDate = policy.effectiveDateWithPendingAmendment();
    this.healthClass = policy.finalRateClass;
    this.flatExtra = policy.flatExtras;
    this.contractState = policy.getApplicationState();
  }
}

export class SafeAnnuity extends SafeBasePolicy {
  constructor(unsafeAnnuity: CoverpathPolicy) {
    super(unsafeAnnuity);

    //the safe classes assume that the owner is a person, but that's not always the case
    //massage the real entity owner into a SafePerson
    if (!this.owner && unsafeAnnuity.ownerEntity) {
      this.owner = {
        firstName: '',
        lastName: '',
        fullName: unsafeAnnuity.ownerEntity.getFullNameOfOwner(),
        gender: '',
        dob: undefined,
        email: '',
        emailName: '',
      } as SafePerson;
    }
  }
}

export class SafeWholePolicy extends SafeBasePolicy {
  hasGIR: boolean;
  gir: SafePolicyRider;

  hasRTR: boolean;
  rtr: SafePolicyRider;

  hasALIR: boolean;
  alir: SafePolicyRider;

  hasExchange1035: boolean;
  exchange1035: SafeExchange1035;

  hasCIR: boolean;
  cir: SafePolicyRider;

  frequencyUnit: string;
  totalPremium: number;
  basePremium: number;

  constructor(unsafeWholePolicy: CoverpathPolicy) {
    super(unsafeWholePolicy);
    this.totalPremium = unsafeWholePolicy.premiumBreakdown.getTotalWithPendingAmendment();
    this.basePremium = unsafeWholePolicy.premiumBreakdown.baseWithPendingAmendment();
    this.loanType = unsafeWholePolicy.loanInterestTypeWithPendingAmendment();
    this.dividendOption = unsafeWholePolicy.dividendOptionWithPendingAmendment();
    this.hasGIR = unsafeWholePolicy.hasGirRiderWithPendingAmendment();
    if (this.hasGIR) {
      this.gir = new SafePolicyRider(unsafeWholePolicy.girRider);
      if (this.hasWOP) {
        this.WOPValue += this.gir.premiumWaiver;
      }
    }
    this.hasRTR = unsafeWholePolicy.hasRtrRiderWithPendingAmendment();
    if (this.hasRTR) {
      this.rtr = new SafePolicyRider(unsafeWholePolicy.rtrRider);
      if (this.hasWOP) {
        this.WOPValue += this.rtr.premiumWaiver;
      }
    }
    this.hasCIR = unsafeWholePolicy.hasCirRiderWithPendingAmendment();
    if (this.hasCIR) {
      this.cir = new SafePolicyRider(unsafeWholePolicy.cirRider);
      if (this.hasWOP) {
        this.WOPValue += this.cir.premiumWaiver;
      }
    }

    const cpPolicy = unsafeWholePolicy as CoverpathBasePolicy;

    this.hasALIR = cpPolicy.hasAlirRiderWithPendingAmendment();
    if (this.hasALIR && !!cpPolicy.alirRider) {
      this.alir = new SafePolicyRider(cpPolicy.alirRider);
    }

    this.hasExchange1035 =
      unsafeWholePolicy.alirRider && unsafeWholePolicy.alirRider.hasAlir1035ExchangeWithPendingAmendment();
    if (this.hasExchange1035) {
      this.exchange1035 = new SafeExchange1035(unsafeWholePolicy.alirRider);
    }
  }
}

export class SafeTermPolicy extends SafeBasePolicy {
  hasECP: boolean;
  isART: boolean;
  artDescription: string;
  base: number;
  totalPremium: number;

  constructor(unsafePolicy: CoverpathPolicy) {
    super(unsafePolicy);
    this.base = unsafePolicy.premiumBreakdown.baseWithPendingAmendment();
    this.hasECP = unsafePolicy.productWithPendingAmendment() === Product.productTypes.Term.productCodes.CASTermECP;
    this.isART = unsafePolicy.termWithPendingAmendment() === 1;
    this.artDescription = Product.products.CASTerm.artDescription;
    this.totalPremium = unsafePolicy.premiumBreakdown.getTotalWithPendingPremiumAmendment();
  }
}

export class SafeQuoteExchange1035 {
  cashValueTransfer: number;
  isMec: boolean;
  costBasis: number;
  expectedArrival: number;
  applyTowards: string;

  constructor(unsafeExchange1035: QuoteExchange1035) {
    this.cashValueTransfer = unsafeExchange1035.cashValueTransfer;
    this.isMec = unsafeExchange1035.isMec;
    this.costBasis = unsafeExchange1035.costBasis;
    this.expectedArrival = unsafeExchange1035.expectedArrival;
    this.applyTowards = unsafeExchange1035.applyTowards;
  }
}

export class SafeBaseQuote {
  effectiveDate: Date;
  faceAmount: number;
  dateOfBirth: Date;
  healthClass: string;
  quoteExchange1035: SafeQuoteExchange1035;
  term: number;
  product: string;
  paymentFrequency: string;

  constructor(unsafeQuote: BaseQuote) {
    this.effectiveDate = unsafeQuote.effectiveDate;
    this.faceAmount = unsafeQuote.faceAmount;
    this.dateOfBirth = unsafeQuote.dateOfBirth;
    this.healthClass = unsafeQuote.healthClass;
    this.term = unsafeQuote.term;
    this.product = unsafeQuote.product;
    this.paymentFrequency = unsafeQuote.paymentFrequency;

    if (!!unsafeQuote.quoteExchange1035) {
      this.quoteExchange1035 = new SafeQuoteExchange1035(unsafeQuote.quoteExchange1035);
    }
  }
}

export class SafeTermQuote extends SafeBaseQuote {
  constructor(unsafeTermQuote: CoverpathTermQuote) {
    super(unsafeTermQuote);
  }
}

export class SafeWholeQuote extends SafeBaseQuote {
  dividendInterestAdjustment: number;
  hasWOP: boolean;
  WOPValue: number;
  dividend: string;
  hasExchange1035: boolean;
  exchange1035: SafeQuoteExchange1035;
  gender: string;
  contractState: string;
  flatExtra: number;
  taxBracket: number;
  loanType: LoanType;

  hasRTR: boolean;
  rtr: SafeQuoteRider;

  hasALIR: boolean;
  alir: SafeQuoteRider;

  hasGIR: boolean;
  gir: SafeQuoteRider;

  hasCIR: boolean;
  cir: SafeQuoteRider;

  bankSale: boolean;

  constructor(unsafeWholeLifeQuote: CoverpathWholeLifeQuote) {
    super(unsafeWholeLifeQuote);
    this.dividendInterestAdjustment = unsafeWholeLifeQuote.dividendInterestAdjustment;
    this.hasWOP = !!unsafeWholeLifeQuote.waiverOfPremium ? unsafeWholeLifeQuote.waiverOfPremium : false;
    this.dividend = unsafeWholeLifeQuote.dividend;
    this.gender = unsafeWholeLifeQuote.gender;
    this.contractState = unsafeWholeLifeQuote.contractState;
    this.flatExtra = unsafeWholeLifeQuote.flatExtra;
    this.taxBracket = unsafeWholeLifeQuote.taxBracket;
    this.loanType = unsafeWholeLifeQuote.loanType;
    this.bankSale = unsafeWholeLifeQuote.bankSale;

    const girRider = (unsafeWholeLifeQuote.riders || []).find((rider) => {
      return rider.type === RiderType.GIR;
    });
    const rtrRider = (unsafeWholeLifeQuote.riders || []).find((rider) => {
      return rider.type === RiderType.RTR;
    });
    const alirRider = (unsafeWholeLifeQuote.riders || []).find((rider) => {
      return rider.type === RiderType.ALIR;
    });
    const cirRider = (unsafeWholeLifeQuote.riders || []).find((rider) => {
      return rider.type === RiderType.CIR;
    });
    this.hasGIR = !!girRider;
    if (this.hasGIR) {
      this.gir = new SafeQuoteRider(girRider);
      if (this.hasWOP) {
        this.WOPValue += this.gir.premiumWaiver;
      }
    }
    this.hasRTR = !!rtrRider;
    if (this.hasRTR) {
      this.rtr = new SafeQuoteRider(rtrRider);
      if (this.hasWOP) {
        this.WOPValue += this.rtr.premiumWaiver;
      }
    }
    this.hasCIR = !!cirRider;
    if (this.hasCIR) {
      this.cir = new SafeQuoteRider(cirRider);
      if (this.hasWOP) {
        this.WOPValue += this.cir.premiumWaiver;
      }
    }

    const cpPolicy = unsafeWholeLifeQuote as BaseQuote;

    this.hasALIR = !!alirRider;

    this.hasExchange1035 = !!unsafeWholeLifeQuote.quoteExchange1035;
    if (this.hasExchange1035) {
      this.exchange1035 = new SafeQuoteExchange1035(unsafeWholeLifeQuote.quoteExchange1035);
    }
  }
}

export class SafeAgent {
  displayName: string;
  displayNameWithLastName: string;
  firstName: string;
  lastName: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  phoneNumber: string;
  producerId: string;
  agencyName: string;
  email: string;

  constructor(unsafeApplication: CasApplication) {
    const agent = unsafeApplication.primaryPolicy.primaryPolicyAgent.primaryAgent;
    this.displayName = agent.displayName;
    this.displayNameWithLastName = agent.displayNameWithLastName;
    this.email = agent.person.email;
    this.firstName = agent.person.firstName;
    this.lastName = agent.person.lastName;
    this.street = agent.address.street;
    this.city = agent.address.city;
    this.state = agent.address.state;
    this.zip = agent.address.zip;
    this.phoneNumber = agent.phone.number;
    this.producerId = agent.producerId;
    this.agencyName = agent.agencyName;
  }
}

export class SafePolicy {
  totalFaceAmount: number;
  totalPremiumAmount: number;
  faceAmount: number;
  product: string;
  WOPValue: number;
  term: string;
  submitted: boolean;

  hasWhole: boolean;
  wholeInfo: SafeWholePolicy;

  hasTerm: boolean;
  termInfo: SafeTermPolicy;

  paymentFrequency: string;

  primaryAgent: SafeAgent;

  ownerEmail: string;
  ownerFullName: string;

  primaryPolicy: SafeBasePolicy;

  constructor(unsafeApplication: CasApplication) {
    this.submitted = !!unsafeApplication.primaryPolicy.submittedAt;
    this.totalFaceAmount = unsafeApplication.getTotalFaceAmountWithPendingAmendment();
    if (
      !!unsafeApplication.primaryPolicyWithPendingAmendment &&
      !!unsafeApplication.primaryPolicyWithPendingAmendment.premiumBreakdown
    ) {
      this.totalPremiumAmount = unsafeApplication.getTotalPremiumWithPendingAmendment();
    }

    this.faceAmount = unsafeApplication.primaryPolicy.faceAmountWithPendingAmendment();
    this.product = unsafeApplication.primaryPolicy.productWithPendingAmendment();

    const termPolicy = (unsafeApplication.secondaryPolicyWithPendingAmendment ||
      unsafeApplication.primaryPolicyWithPendingAmendment) as CoverpathPolicy;
    const wholeLifePolicy = unsafeApplication.primaryPolicyWithPendingAmendment as CoverpathPolicy;

    this.hasWhole = unsafeApplication.primaryPolicyWithPendingAmendment.isWholeLifePolicyWithPendingAmendment();
    this.hasTerm =
      unsafeApplication.primaryPolicyWithPendingAmendment.isTermLifePolicyWithPendingAmendment() ||
      !!unsafeApplication.secondaryPolicyWithPendingAmendment;

    this.paymentFrequency = unsafeApplication.primaryPolicy.paymentFrequencyWithPendingAmendment();

    this.primaryAgent = new SafeAgent(unsafeApplication);

    if (this.hasWhole) {
      this.wholeInfo = new SafeWholePolicy(wholeLifePolicy);
      if (unsafeApplication.primaryPolicyWithPendingAmendment === wholeLifePolicy) {
        this.primaryPolicy = this.wholeInfo;
      }
    }

    if (this.hasTerm) {
      this.termInfo = new SafeTermPolicy(termPolicy);
      if (unsafeApplication.primaryPolicyWithPendingAmendment === termPolicy) {
        this.primaryPolicy = this.termInfo;
      }
    }

    if (!(this.hasWhole || this.hasTerm)) {
      this.primaryPolicy = new SafeAnnuity(unsafeApplication.primaryPolicy);
    }
  }
}
